import ExcelJS from 'exceljs';
import { downloadBlob } from 'utils/preview-export';
import { APIData } from 'types/cms/frontendTypes';
import { getLocaleValue } from './RecoTableColumns';

type Reco = APIData<'api::recommendation.recommendation'>;

export interface RecoExportContext {
  selectedLocale: string;
  applianceCategories: APIData<'api::appliance-category.appliance-category'>[];
  isResidential: boolean;
  isSmb: boolean;
}

interface ExportColumnSpec {
  header: string;
  locked: boolean;
  width?: number;
  align?: 'center';
  getValue?: (reco: Reco, ctx: RecoExportContext) => string | number | undefined;
  formula?: (row: number) => string;
}

const DEFAULT_COLUMN_WIDTH = 16;

const yn = (v: boolean | undefined | null): string => (v ? 'Y' : 'N');
const yesOrBlank = (v: boolean | undefined | null): string | undefined => (v ? 'Yes' : undefined);
const fuelTypeIncludes = (reco: Reco, fuel: string): string =>
  yn((reco.fuelType as unknown as string[] | undefined)?.includes(fuel));

const EXPORT_COLUMNS: ExportColumnSpec[] = [
  { header: 'RECO ID', locked: true, width: 16, getValue: reco => reco.recommendationId },
  { header: 'Category', locked: true, width: 14, getValue: (reco, ctx) => ctx.applianceCategories.find(cat => cat.categoryId === reco.category)?.name ?? reco.category },
  { header: 'Original\nLocale', locked: true, width: 12, getValue: reco => reco.locale },
  { header: 'Utility Defined Locale', locked: false, width: 14 },
  { header: 'Original\ntitle', locked: true, width: 32, getValue: reco => reco.title },
  { header: 'Utility Edited Title', locked: false, width: 32 },
  { header: 'Char\n(35)', locked: true, width: 8, align: 'center', formula: row => `IF(LEN(F${row})=0,"",LEN(F${row}))` },
  { header: 'Original Description', locked: true, width: 45, getValue: reco => reco.description },
  { header: 'Utility Edited Description', locked: false, width: 45 },
  { header: 'Char\n(105)', locked: true, width: 8, align: 'center', formula: row => `IF(LEN(I${row})=0,"",LEN(I${row}))` },
  { header: 'Sources', locked: true, width: 18 },
  { header: 'Featured Content', locked: false, width: 12, align: 'center', getValue: reco => yn(reco.featuredContent) },
  { header: 'Program', locked: false, width: 10, align: 'center', getValue: reco => yn(reco.program) },
  { header: 'CallToAction\n(12)', locked: false, width: 16, getValue: (reco, ctx) => getLocaleValue(reco, 'ctaButtonText', ctx.selectedLocale) as string },
  { header: 'CTA Link', locked: false, width: 28, getValue: reco => reco.ctaButtonLink },
  { header: 'Icon', locked: true, width: 32, getValue: reco => reco.icon?.url },
  { header: 'Live Images - Thumbnail Res', locked: true, width: 32, getValue: reco => reco.fullImage?.url },
  { header: 'Live Images - Elongated Res', locked: true, width: 32, getValue: reco => reco.elongatedImage?.url },
  { header: 'Live Images - Shortened Res\n(For Featured Content in Emails and HERs)', locked: true, width: 32, getValue: reco => reco.shortenedImage?.url },
  { header: 'Paper Channel (Y/N)', locked: true, width: 12, align: 'center', getValue: reco => yn(reco.channelPaper) },
  { header: 'EMail/Web Channel (Y/N)', locked: true, width: 14, align: 'center', getValue: reco => yn(reco.channelEmail) },
  { header: 'Detailed Content', locked: true, width: 90, getValue: reco => reco.detail },
  { header: 'Customer Detailed Content', locked: false, width: 90 },
  { header: 'Youtube URL', locked: true, width: 28, getValue: reco => reco.videoLink },
  { header: 'Customer Youtube URL', locked: false, width: 28 },
  { header: 'Detailed Recommendation ImageURL', locked: true, width: 32 },
  { header: 'Detailed Recommendation Title', locked: true, width: 28 },
  { header: 'Customer Detailed Recommendation Title', locked: false, width: 28 },
  { header: 'Suitable for RESIDENTIAL (Y/N)', locked: true, width: 12, align: 'center', getValue: (_reco, ctx) => yn(ctx.isResidential) },
  { header: 'Suitable for SMB (Y/N)', locked: true, width: 12, align: 'center', getValue: (_reco, ctx) => yn(ctx.isSmb) },
  { header: 'Suitable for ELECTRICITY (Y/N)', locked: true, width: 12, align: 'center', getValue: reco => fuelTypeIncludes(reco, 'ELECTRIC') },
  { header: 'Suitable for GAS (Y/N)', locked: true, width: 12, align: 'center', getValue: reco => fuelTypeIncludes(reco, 'GAS') },
  { header: 'Suitable for WATER (Y/N)', locked: true, width: 12, align: 'center', getValue: reco => fuelTypeIncludes(reco, 'WATER') },
  { header: 'Disable? (Select Yes or leave blank)', locked: true, width: 16, getValue: reco => yesOrBlank(reco.disabled) },
  { header: 'Default Recommendation? (Select Yes or leave blank)', locked: true, width: 18, getValue: reco => yesOrBlank(reco.defaultReco) },
  { header: 'Additional Comments', locked: true, width: 32 },
];

// Column index (1-based) through which panes stay frozen while scrolling — "Original Description" (col H).
const FREEZE_THROUGH_COLUMN = 8;

// Pastel Office theme fills: yellow for locked/reference headers, green for utility-editable headers.
const LOCKED_HEADER_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
const EDITABLE_HEADER_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };
const THIN_BLACK_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FF000000' } },
  left: { style: 'thin', color: { argb: 'FF000000' } },
  bottom: { style: 'thin', color: { argb: 'FF000000' } },
  right: { style: 'thin', color: { argb: 'FF000000' } },
};

export async function exportRecosXlsx(recos: Reco[], ctx: RecoExportContext, filename: string): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Recommendations');
  sheet.columns = EXPORT_COLUMNS.map(col => ({ header: col.header, width: col.width ?? DEFAULT_COLUMN_WIDTH }));

  sheet.views = [{ state: 'frozen', xSplit: FREEZE_THROUGH_COLUMN, ySplit: 1 }];
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: EXPORT_COLUMNS.length } };

  const headerRow = sheet.getRow(1);
  headerRow.height = 45;
  headerRow.eachCell((cell, colIdx) => {
    cell.protection = { locked: true };
    cell.font = { bold: true, size: 11 };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.fill = EXPORT_COLUMNS[colIdx - 1].locked ? LOCKED_HEADER_FILL : EDITABLE_HEADER_FILL;
    cell.border = THIN_BLACK_BORDER;
  });

  recos.forEach((reco, i) => {
    const rowNum = i + 2;
    const row = sheet.getRow(rowNum);
    EXPORT_COLUMNS.forEach((col, colIdx) => {
      const cell = row.getCell(colIdx + 1);
      cell.value = col.formula ? { formula: col.formula(rowNum) } : (col.getValue?.(reco, ctx) ?? '');
      cell.protection = { locked: col.locked };
      cell.font = { size: 9 };
      cell.alignment = {
        wrapText: true,
        vertical: col.align === 'center' ? 'middle' : 'top',
        horizontal: col.align,
      };
      cell.border = THIN_BLACK_BORDER;
    });
  });

  await sheet.protect('', { selectLockedCells: true, selectUnlockedCells: true });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}
